(() => {
  const GLOBAL_KEY = '__cubittInstagramPostsCarousel';
  const ROOT_SELECTOR = '[data-instagram-posts]';
  const DESKTOP_QUERY = '(min-width: 990px)';

  const previousApi = window[GLOBAL_KEY];

  previousApi?.teardownGlobalListeners?.();
  if (previousApi?.destroy) {
    previousApi.destroy(document);
  } else {
    previousApi?.destroyAll?.();
  }

  const instances = new Set();

  class InstagramPostsCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-instagram-posts-viewport]');
      this.track = root.querySelector('[data-instagram-posts-track]');
      this.previousButton = root.querySelector('[data-instagram-posts-previous]');
      this.nextButton = root.querySelector('[data-instagram-posts-next]');
      this.desktopMedia = window.matchMedia(DESKTOP_QUERY);
      this.originalItems = [];
      this.cloneItems = [];
      this.currentIndex = 0;
      this.itemStep = 0;
      this.isAnimating = false;
      this.resizeObserver = null;
      this.refreshTimer = null;
      this.lastLayoutMode = null;
      this.lastViewportWidth = 0;

      if (!this.viewport || !this.track) return;

      this.bindEvents();
      this.observe();
      this.refresh();
    }

    bindEvents() {
      this.onMediaChange = () => this.scheduleRefresh();
      this.onPreviousClick = () => this.move(-1);
      this.onNextClick = () => this.move(1);
      this.onTransitionEnd = (event) => {
        if (event.target !== this.track || event.propertyName !== 'transform') return;
        this.finishTransition();
      };
      this.onKeyDown = (event) => {
        if (!this.desktopMedia.matches) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(1);
        }
      };
      this.onMouseEnter = () => this.root.classList.add('is-controls-visible');
      this.onMouseLeave = () => this.root.classList.remove('is-controls-visible');
      this.onFocusIn = () => this.root.classList.add('is-controls-visible');
      this.onFocusOut = (event) => {
        if (!this.root.contains(event.relatedTarget)) {
          this.root.classList.remove('is-controls-visible');
        }
      };

      this.desktopMedia.addEventListener?.('change', this.onMediaChange);
      this.track.addEventListener('transitionend', this.onTransitionEnd);
      this.viewport.addEventListener('keydown', this.onKeyDown);
      this.viewport.addEventListener('mouseenter', this.onMouseEnter);
      this.viewport.addEventListener('mouseleave', this.onMouseLeave);
      this.viewport.addEventListener('focusin', this.onFocusIn);
      this.viewport.addEventListener('focusout', this.onFocusOut);
      this.previousButton?.addEventListener('click', this.onPreviousClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
    }

    observe() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleRefresh());
        this.resizeObserver.observe(this.viewport);
      } else {
        this.onWindowResize = () => this.scheduleRefresh();
        window.addEventListener('resize', this.onWindowResize, { passive: true });
      }

      this.track.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', () => this.scheduleRefresh(), { once: true });
        }
      });
    }

    scheduleRefresh() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refresh(), 80);
    }

    getRealItems() {
      return Array.from(this.track.children).filter(
        (item) => !item.hasAttribute('data-instagram-posts-clone')
      );
    }

    makeClone(item) {
      const clone = item.cloneNode(true);
      clone.setAttribute('data-instagram-posts-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.removeAttribute('id');

      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
        node.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });

      if ('inert' in clone) clone.inert = true;

      return clone;
    }

    removeClones() {
      this.cloneItems.forEach((clone) => clone.remove());
      this.cloneItems = [];
    }

    resetTrack() {
      this.track.classList.remove('is-animating');
      this.track.style.transform = '';
      this.isAnimating = false;
      this.itemStep = 0;
      this.currentIndex = 0;
      this.viewport.classList.remove('is-carousel-ready');
      this.root.classList.remove('is-controls-visible');
      this.setControls(false);
    }

    setupDesktop() {
      this.removeClones();
      this.resetTrack();
      this.originalItems = this.getRealItems();

      if (this.originalItems.length < 2) return;

      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      this.originalItems.forEach((item, index) => {
        item.setAttribute('data-instagram-posts-real-index', String(index));

        const beforeClone = this.makeClone(item);
        const afterClone = this.makeClone(item);
        beforeFragment.appendChild(beforeClone);
        afterFragment.appendChild(afterClone);
        this.cloneItems.push(beforeClone, afterClone);
      });

      this.track.insertBefore(beforeFragment, this.originalItems[0]);
      this.track.appendChild(afterFragment);

      window.requestAnimationFrame(() => {
        const children = Array.from(this.track.children);
        const first = children[0];
        const second = children[1];

        if (!first || !second) return;

        this.itemStep = second.offsetLeft - first.offsetLeft;
        this.currentIndex = this.originalItems.length;
        this.setPosition(false);
        this.viewport.classList.add('is-carousel-ready');
        this.setControls(true);
      });
    }

    setupMobile() {
      const preservedScrollLeft = Math.max(0, this.viewport.scrollLeft);

      this.removeClones();
      this.resetTrack();
      this.originalItems = this.getRealItems();
      this.originalItems.forEach((item) => item.removeAttribute('data-instagram-posts-real-index'));

      window.requestAnimationFrame(() => {
        const maxScrollLeft = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        this.viewport.scrollLeft = Math.min(preservedScrollLeft, maxScrollLeft);
      });
    }

    refresh() {
      if (!this.viewport.isConnected) return;

      const layoutMode = this.desktopMedia.matches ? 'desktop' : 'mobile';
      const viewportWidth = Math.round(this.viewport.clientWidth);

      if (
        layoutMode === this.lastLayoutMode &&
        Math.abs(viewportWidth - this.lastViewportWidth) < 2
      ) {
        return;
      }

      this.lastLayoutMode = layoutMode;
      this.lastViewportWidth = viewportWidth;

      if (layoutMode === 'desktop') {
        this.setupDesktop();
      } else {
        this.setupMobile();
      }
    }

    setControls(enabled) {
      [this.previousButton, this.nextButton].forEach((button) => {
        if (!button) return;
        button.disabled = !enabled;
        button.setAttribute('aria-hidden', enabled ? 'false' : 'true');
        button.tabIndex = enabled ? 0 : -1;
      });
    }

    setPosition(animate) {
      if (!this.itemStep) return;

      if (animate) {
        this.track.classList.add('is-animating');
        this.isAnimating = true;
      } else {
        this.track.classList.remove('is-animating');
        this.isAnimating = false;
      }

      this.track.style.transform = `translate3d(${-this.currentIndex * this.itemStep}px, 0, 0)`;
    }

    move(direction) {
      if (!this.desktopMedia.matches || this.originalItems.length < 2 || this.isAnimating) return;

      this.currentIndex += direction;
      this.setPosition(true);

    }

    finishTransition() {
      const count = this.originalItems.length;
      if (!count) return;

      this.isAnimating = false;
      this.track.classList.remove('is-animating');

      if (this.currentIndex >= count * 2) {
        this.currentIndex = count;
        this.setPosition(false);
      } else if (this.currentIndex < count) {
        this.currentIndex = count * 2 - 1;
        this.setPosition(false);
      }
    }

    scrollToSelectedBlock(blockElement) {
      if (!blockElement || !this.root.contains(blockElement)) return;

      if (this.desktopMedia.matches) {
        const index = this.originalItems.indexOf(blockElement);
        if (index < 0) return;
        this.currentIndex = this.originalItems.length + index;
        this.setPosition(true);
      }
    }

    destroy() {
      window.clearTimeout(this.refreshTimer);
      this.removeClones();
      this.resetTrack();
      this.desktopMedia.removeEventListener?.('change', this.onMediaChange);
      this.track.removeEventListener('transitionend', this.onTransitionEnd);
      this.viewport.removeEventListener('keydown', this.onKeyDown);
      this.viewport.removeEventListener('mouseenter', this.onMouseEnter);
      this.viewport.removeEventListener('mouseleave', this.onMouseLeave);
      this.viewport.removeEventListener('focusin', this.onFocusIn);
      this.viewport.removeEventListener('focusout', this.onFocusOut);
      this.previousButton?.removeEventListener('click', this.onPreviousClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.resizeObserver?.disconnect();

      if (this.onWindowResize) {
        window.removeEventListener('resize', this.onWindowResize);
      }
    }
  }

  function initialize(scope = document) {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach((root) => {
      if (root.__cubittInstagramCarousel) {
        root.__cubittInstagramCarousel.destroy();
        instances.delete(root.__cubittInstagramCarousel);
      }

      const instance = new InstagramPostsCarousel(root);
      root.__cubittInstagramCarousel = instance;
      instances.add(instance);
    });
  }

  function destroy(scope) {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach((root) => {
      const instance = root.__cubittInstagramCarousel;
      if (!instance) return;
      instance.destroy();
      instances.delete(instance);
      delete root.__cubittInstagramCarousel;
    });
  }

  function destroyAll() {
    Array.from(instances).forEach((instance) => instance.destroy());
    instances.clear();
  }

  function handleSectionLoad(event) {
    initialize(event.target);
  }

  function handleSectionUnload(event) {
    destroy(event.target);
  }

  function handleBlockSelect(event) {
    const block = event.target.closest?.('[data-instagram-posts-item]');
    const root = block?.closest?.(ROOT_SELECTOR);
    root?.__cubittInstagramCarousel?.scrollToSelectedBlock(block);
  }

  function teardownGlobalListeners() {
    document.removeEventListener('shopify:section:load', handleSectionLoad);
    document.removeEventListener('shopify:section:unload', handleSectionUnload);
    document.removeEventListener('shopify:block:select', handleBlockSelect);
  }

  const api = {
    initialize,
    destroy,
    destroyAll,
    teardownGlobalListeners
  };

  window[GLOBAL_KEY] = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  document.addEventListener('shopify:block:select', handleBlockSelect);
})();
